import { formatDistanceToNow } from 'date-fns';
import { AlertTriangle, ArrowLeft, Wrench } from 'lucide-react';
import { Badge } from '../components/Badge';
import {
  ACTIVE_VOICE_CALL_STATUSES,
  useAdminVoiceCallAudio,
  useAdminVoiceCallDetail,
} from '../hooks/useAdminData';
import { navigate } from '../lib/navigation';
import { VOICE_CALL_STATUS_VARIANT } from '../lib/voiceCallStatus';

const TOOL_CALL_STATUS_VARIANT: Record<string, 'success' | 'danger' | 'default'> = {
  completed: 'success',
  failed: 'danger',
};

function formatDuration(seconds: number | null) {
  if (seconds == null) return '-';
  if (seconds < 60) return `${seconds}s`;
  return `${(seconds / 60).toFixed(1)}m`;
}

function formatLatency(ms: number | null) {
  if (ms == null) return '-';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

interface VoiceCallDetailProps {
  id: string;
}

export function VoiceCallDetail({ id }: VoiceCallDetailProps) {
  const { data: call, isLoading } = useAdminVoiceCallDetail(id);
  const { data: audio } = useAdminVoiceCallAudio(id);

  if (isLoading || !call) {
    return (
      <div role="status" className="text-text-muted p-5">
        Loading...
      </div>
    );
  }

  const statusVariant = VOICE_CALL_STATUS_VARIANT[call.status];
  const isActive = ACTIVE_VOICE_CALL_STATUSES.includes(call.status);

  const failureWebhookEvents = call.webhookEvents.filter(
    (event) => event.source === 'dispatch_failure' || event.source === 'relay_dispatch_failure',
  );
  const hasFailureCause =
    call.errorCode != null || call.errorMessage != null || failureWebhookEvents.length > 0;

  const timelineEvents = [...call.webhookEvents]
    .filter((event) => event.source === 'twilio_status' || event.source === 'convai_post_call')
    .sort((a, b) => new Date(a.receivedAt).getTime() - new Date(b.receivedAt).getTime());

  return (
    <div className="space-y-6">
      <button
        type="button"
        onClick={() => navigate('/voice-calls')}
        className="flex items-center gap-1.5 text-sm text-text-secondary hover:text-primary transition-colors"
      >
        <ArrowLeft size={16} aria-hidden="true" />
        Back to Voice Calls
      </button>

      {isActive && (
        <div role="status" className="sr-only">
          Call is in progress and updates automatically.
        </div>
      )}

      {hasFailureCause && (
        <div
          data-testid="voice-call-failure-cause"
          role="alert"
          className="rounded-xl border border-danger/30 bg-danger/10 p-5 space-y-2"
        >
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-danger" aria-hidden="true" />
            <h2 className="text-sm font-semibold text-danger">Call Failed</h2>
          </div>
          {(call.errorCode || call.errorMessage) && (
            <p className="text-sm text-danger">
              {call.errorCode ? `[${call.errorCode}] ` : ''}
              {call.errorMessage}
            </p>
          )}
          {failureWebhookEvents.map((event) => (
            <p key={event.id} className="text-sm text-danger">
              {event.errorMessage}
            </p>
          ))}
        </div>
      )}

      <div className="bg-surface rounded-xl border border-border p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-semibold text-text">{call.id}</h1>
          <Badge variant={statusVariant}>{call.status}</Badge>
        </div>
        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <div>
            <dt className="text-text-muted text-xs">Organization</dt>
            <dd className="text-text">{call.organization?.name ?? call.organizationId}</dd>
          </div>
          <div>
            <dt className="text-text-muted text-xs">Lead</dt>
            <dd className="text-text">{call.lead?.fullName ?? call.lead?.phone ?? call.leadId}</dd>
          </div>
          <div>
            <dt className="text-text-muted text-xs">Agent</dt>
            <dd className="text-text">{call.voiceAgent?.name ?? '-'}</dd>
          </div>
          <div>
            <dt className="text-text-muted text-xs">Outcome</dt>
            <dd className="text-text">{call.outcome ?? '-'}</dd>
          </div>
          <div>
            <dt className="text-text-muted text-xs">Duration</dt>
            <dd className="text-text">{formatDuration(call.durationSeconds)}</dd>
          </div>
          <div>
            <dt className="text-text-muted text-xs">Created</dt>
            <dd className="text-text">
              {formatDistanceToNow(new Date(call.createdAt), { addSuffix: true })}
            </dd>
          </div>
          {call.triggeringExecutionId && (
            <div>
              <dt className="text-text-muted text-xs">Triggering Execution</dt>
              <dd className="text-text">
                {call.triggeringExecutionType}: {call.triggeringExecutionId}
              </dd>
            </div>
          )}
        </dl>
      </div>

      <div className="bg-surface rounded-xl border border-border p-5 space-y-3">
        <h2 className="text-sm font-medium text-text-secondary">Audio Replay</h2>
        {audio?.url ? (
          <audio
            data-testid="voice-call-audio"
            controls
            aria-label="Call recording audio player"
            className="w-full"
          >
            <source src={audio.url} />
          </audio>
        ) : (
          <p className="text-sm text-text-muted">No recording available</p>
        )}
        {audio?.dualChannelUrl && (
          <div>
            <p className="text-xs text-text-muted mb-1">Dual-channel recording</p>
            <audio
              data-testid="voice-call-audio-dual-channel"
              controls
              aria-label="Dual-channel call recording audio player"
              className="w-full"
            >
              <source src={audio.dualChannelUrl} />
            </audio>
          </div>
        )}
      </div>

      <div
        data-testid="voice-call-timeline"
        className="bg-surface rounded-xl border border-border"
      >
        <div className="px-5 py-4 border-b border-border">
          <h2 className="text-sm font-medium text-text-secondary">Status Transition Timeline</h2>
        </div>
        <ul className="divide-y divide-border">
          {timelineEvents.length > 0 ? (
            timelineEvents.map((event) => (
              <li key={event.id} className="px-5 py-3 flex items-center justify-between gap-4">
                <span className="text-sm text-text">
                  {JSON.stringify(event.rawPayload)}
                </span>
                <span className="text-xs text-text-muted whitespace-nowrap">
                  {formatDistanceToNow(new Date(event.receivedAt), { addSuffix: true })}
                </span>
              </li>
            ))
          ) : (
            <li className="px-5 py-8 text-center text-text-muted text-sm">
              No provider events recorded
            </li>
          )}
        </ul>
      </div>

      <div
        data-testid="voice-call-transcript"
        className="bg-surface rounded-xl border border-border"
      >
        <div className="px-5 py-4 border-b border-border">
          <h2 className="text-sm font-medium text-text-secondary">Transcript</h2>
        </div>
        <ul className="divide-y divide-border">
          {call.transcriptEntries.length > 0 ? (
            [...call.transcriptEntries]
              .sort((a, b) => a.turnIndex - b.turnIndex)
              .map((entry) => (
                <li key={entry.id} className="px-5 py-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-text-secondary uppercase">
                      {entry.speaker}
                    </span>
                    {entry.sentiment && (
                      <span className="text-xs text-text-muted">({entry.sentiment})</span>
                    )}
                  </div>
                  <p className="text-sm text-text mt-0.5">{entry.text}</p>
                </li>
              ))
          ) : (
            <li className="px-5 py-8 text-center text-text-muted text-sm">No transcript</li>
          )}
        </ul>
      </div>

      <div
        data-testid="voice-call-tool-calls"
        className="bg-surface rounded-xl border border-border"
      >
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <Wrench size={16} className="text-primary" aria-hidden="true" />
          <h2 className="text-sm font-medium text-text-secondary">Tool-Call Logs</h2>
          <span className="text-xs text-text-muted">({call.toolCalls.length} calls)</span>
        </div>
        <ul className="divide-y divide-border">
          {call.toolCalls.length > 0 ? (
            [...call.toolCalls]
              .sort((a, b) => a.turnIndex - b.turnIndex)
              .map((toolCall) => {
                const variant = TOOL_CALL_STATUS_VARIANT[toolCall.status] ?? 'default';
                return (
                  <li
                    key={toolCall.id}
                    className="px-5 py-3 flex items-center justify-between gap-4"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm text-text font-medium truncate">
                        {toolCall.toolName}
                      </span>
                      <Badge variant={variant}>{toolCall.status}</Badge>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-text-muted whitespace-nowrap">
                      {toolCall.errorMessage && (
                        <span className="text-danger">{toolCall.errorMessage}</span>
                      )}
                      <span>{formatLatency(toolCall.latencyMs)}</span>
                    </div>
                  </li>
                );
              })
          ) : (
            <li className="px-5 py-8 text-center text-text-muted text-sm">No tool calls</li>
          )}
        </ul>
      </div>
    </div>
  );
}
